<template>
  <lb-dialog
    v-model="dialogVisible"
    heading="Pending Payouts to Societies"
    width="70%"
    data-testid="pending-payouts-modal__dialog"
  >
    <template v-slot:body>
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        height="2"
        class="mb-0 pending-payouts__loader"
      />
      <!-- Header Summary Section -->
      <div
        v-if="!loading && records.length"
        class="pending-payouts__summary mb-3"
        data-testid="pending-payouts-modal__summary"
      >
        <div class="count-card gap-8" data-testid="pending-payouts-modal__total-amount-card">
          <h6 class="count-card__label font-weight-medium mr-3">Total Amount</h6>
          <h4 class="count-card__value font-weight-medium">{{ formattedTotalAmount }}</h4>
        </div>
        <div class="count-card gap-8" data-testid="pending-payouts-modal__total-records-chip">
          <h6 class="count-card__label font-weight-medium mr-3">Total Records</h6>
          <h4 class="count-card__value font-weight-medium">{{ totalCount.toLocaleString('en-IN') }}</h4>
        </div>
        <v-spacer />
        <v-btn
          icon
          small
          :loading="loading"
          title="Refresh data"
          @click="fetchData({ forceRefresh: true })"
          data-testid="pending-payouts-modal__refresh-btn"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
        <DownloadExcel
          button-text="Generate Excel"
          button-color="white"
          test-id-prefix="pending-payouts-modal__download-excel"
          :download-history="filteredDownloadHistory"
          :loading-history="loadingHistory"
          :current-user-id="$store.state.user ? String($store.state.user._id || '') : ''"
          :compact="true"
          @generate="handleGenerateExcel"
          @refresh="refreshDownloadHistory"
          @download="handleDownloadFile($event)"
        />
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="pending-payouts__loading d-flex flex-column align-center justify-center pa-8"
        data-testid="pending-payouts-modal__loading"
      >
        <v-progress-circular indeterminate color="primary" size="48" />
        <span class="mt-4 grey--text">Generating report... This may take a moment.</span>
      </div>


      <!-- Flat Records Table -->
      <template v-else-if="records.length">
        <lb-datatableaction
          ref="dataTableAction"
          :headers="tableHeaders"
          :tabledata="filteredRecords"
          :displayedRowCount="filteredRecords.length"
          :autoHeight="true"
          :bulkActions="false"
          :showToolbar="true"
          @filter-item="filterData"
          @clear-item="clearFilter"
          data-testid="pending-payouts-modal__table"
        >
          <template v-slot:tbody>
            <tr v-for="(row, idx) in filteredRecords" :key="idx">
              <td
                v-for="key in rawColumnKeys"
                :key="key"
                class="pending-payouts__cell"
              >
                {{ formatCell(resolveCell(row, key, idx)) }}
              </td>
            </tr>
          </template>
        </lb-datatableaction>

        <!-- Pagination -->
        <div class="pending-payouts__pagination d-flex align-center justify-space-between mt-3">
          <span class="caption grey--text">
            Showing {{ filteredRecords.length }} of {{ totalCount.toLocaleString('en-IN') }} records
            <template v-if="reportPeriodLabel">
              &nbsp;·&nbsp; Report period: {{ reportPeriodLabel }}
              &nbsp;·&nbsp; End date = min(Analysis end, Bank statement end)
            </template>
          </span>
          <v-pagination
            v-model="page"
            :length="totalPages"
            :total-visible="7"
            @input="onPageChange"
            data-testid="pending-payouts-modal__pagination"
          />
        </div>
      </template>

      <!-- No Collection / Re-run State -->
      <div
        v-else-if="!collection"
        class="text-center grey--text pa-8"
        data-testid="pending-payouts-modal__no-collection"
      >
        <v-icon large class="mb-3" color="grey lighten-1">mdi-database-off-outline</v-icon>
        <div class="subtitle-1 mb-1">No data available</div>
        <div class="body-2">Please re-run the analysis to generate Pending Payouts data.</div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center grey--text pa-4"
        data-testid="pending-payouts-modal__empty"
      >
        No pending payout records found for this date.
      </div>
    </template>

    <template v-slot:actions>
      <v-spacer />
      <base-button
        secondary
        label="Close"
        @click="close"
        data-testid="pending-payouts-modal__close-btn"
      />
    </template>
  </lb-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DownloadExcel from '../TransationMatching/downloadExcel.vue';

export default {
  name: 'PendingPayoutsModal',

  components: {
    DownloadExcel
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    collection: {
      type: String,
      default: null
    },
    reportDate: {
      type: String,
      default: null
    },
    analysisDetails: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      columnHeaders: [],
      loading: false,
      loadingHistory: false,
      records: [],
      tableFilters: {},
      totalCount: 0,
      totalAmount: 0,
      page: 1,
      limit: 50,
      totalPages: 0,
      pollInterval: null
    };
  },

  computed: {
    ...mapGetters('transactionMatching', ['downloadHistory']),

    filteredDownloadHistory() {
      return this.downloadHistory.filter(
        file => !Array.isArray(file.tables) || file.tables.length === 0 || file.tables.includes('Pending Payouts')
      );
    },

    formattedTotalAmount() {
      return this.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    rawColumnKeys() {
      return this.columnHeaders;
    },

    filteredRecords() {
      const filters = this.tableFilters;
      if (!filters || Object.keys(filters).length === 0) return this.records;
      return this.records.filter(row => {
        return Object.entries(filters).every(([key, values]) => {
          if (!values || values.length === 0) return true;
          const cellStr = String(row[key] !== null && row[key] !== undefined ? row[key] : '');
          return values.some(v => String(v) === cellStr);
        });
      });
    },

    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    reportPeriodLabel() {
      const start = this.analysisDetails?.startdate;
      const end = this.analysisDetails?.enddate;
      if (!start && !end) return null;
      const fmt = d => this.$nova.formatDate(d, 'DD MMM YYYY');
      if (start && end) return `${fmt(start)} – ${fmt(end)}`;
      if (start) return `From ${fmt(start)}`;
      return `Until ${fmt(end)}`;
    },

    tableHeaders() {
      return this.rawColumnKeys.map(key => ({
        text: this.formatHeaderText(key),
        value: key,
        sortable: false,
        visible: true,
        filterable: true,
        align: 'left'
      }));
    }
  },

  watch: {
    value(val) {
      if (val) {
        this.page = 1;
        if (this.collection) {
          this.fetchData();
          this.refreshDownloadHistory();
        }
      } else {
        this.reset();
      }
    }
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    ...mapActions('transactionMatching', ['fetchPendingPayoutsReport', 'generateResult', 'fetchDownloadHistory']),

    filterData(filters) {
      this.tableFilters = filters || {};
    },

    clearFilter() {
      this.tableFilters = {};
    },

    formatHeaderText(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    buildDataFilters() {
      const pipeline = [];

      // Add report date filter (matches either Record_report_date or report_date)
      if (this.reportDate) {
        // Accept YYYY-MM-DD (primary) or DD-MM-YYYY (converted to YYYY-MM-DD)
        let normalizedDate = this.reportDate;
        if (/^\d{2}-\d{2}-\d{4}$/.test(this.reportDate)) {
          const [d, m, y] = this.reportDate.split('-');
          normalizedDate = `${y}-${m}-${d}`;
        }
        pipeline.push({
          $match: {
            $or: [
              { Record_report_date: { $eq: normalizedDate } },
              { report_date: { $eq: normalizedDate } }
            ]
          }
        });
      }

      // TODO: Add table filters from lb-datatableaction when needed
      // if (this.tableFilters && Object.keys(this.tableFilters).length > 0) {
      //   const matchConditions = {};
      //   for (const [key, values] of Object.entries(this.tableFilters)) {
      //     if (values && values.length > 0) {
      //       matchConditions[key] = values.length === 1
      //         ? { $eq: values[0] }
      //         : { $in: values };
      //     }
      //   }
      //   if (Object.keys(matchConditions).length > 0) {
      //     pipeline.push({ $match: matchConditions });
      //   }
      // }

      return pipeline.length > 0 ? JSON.stringify(pipeline) : null;
    },

    async handleGenerateExcel() {
      const botId = this.analysisDetails?.bpid;
      if (!botId) return;
      try {
        await this.generateResult({
          item: 'Pending Payouts',
          botId,
          collection: this.collection,
          tables: ['Pending Payouts'],
          dataFilters: this.buildDataFilters()
        });
      } catch {
        // Store already surfaces the error via snackbar; swallow here to prevent unhandled rejection
      }
    },

    async refreshDownloadHistory() {
      const botId = this.analysisDetails?.bpid;
      if (!botId) return;
      this.loadingHistory = true;
      try {
        await this.fetchDownloadHistory(botId);
      } finally {
        this.loadingHistory = false;
      }
    },

    handleDownloadFile(file) {
      if (file && file.upload) {
        this.$nova.downloadFile(file.upload);
      } else {
        this.$store.commit('sbError', 'File not available for download', { root: true });
      }
    },

    /**
     * Resolve a display value from a record using the column key.
     * Handles field name differences between record source types.
     */
    resolveCell(row, key, idx) {
      switch (key) {
        case 'sno':
          return String((this.page - 1) * this.limit + idx + 1);
        case 'society':
          return row.mygate_society_id ?? row.society_id;
        case 'name':
          return row.name;
        case 'location':
          return row.location;
        case 'date':
          return row.date || row.internal_report_date;
        case 'reference':
          return row.reference || row.request_id || row.txnid;
        case 'amount':
          return row.amount;
        case 'status':
          return row.status || row.payout_status;
        default:
          return row[key];
      }
    },

    async fetchData({ forceRefresh = false } = {}) {
      this.loading = true;
      try {
        const result = await this.fetchPendingPayoutsReport({
          collection: this.collection,
          reportDate: this.reportDate,
          page: this.page,
          limit: this.limit,
          client: this.analysisDetails.client,
          tmaid: this.analysisDetails._id,
          bpid: this.analysisDetails.bpid,
          forceRefresh
        });

        if (result.reportStatus === 'ready') {
          this.columnHeaders = result.headers || [];
          this.records = result.records || [];
          this.totalCount = result.totalCount || 0;
          this.totalAmount = result.totalAmount != null
            ? result.totalAmount
            : this.records.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          this.totalPages = result.totalPages || 0;
          this.stopPolling();
          this.loading = false;
        } else if (result.reportStatus === 'processing') {
          this.startPolling();
          // Keep loading = true while processing
        }
      } catch (err) {
        this.loading = false;
        this.stopPolling();
        this.$store.commit('sbError', err.message || 'Failed to load pending payouts', { root: true });
      }
    },

    onPageChange(newPage) {
      this.page = newPage;
      this.fetchData();
    },

    formatCell(value) {
      if (value === null || value === undefined || value === 'nan') return '–';
      if (typeof value === 'number') {
        return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return value;
    },

    startPolling() {
      if (this.pollInterval) return;
      this.pollInterval = setInterval(() => this.fetchData(), 10000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    getStatusChipStyle(status) {
      const s = String(status || '').trim().toUpperCase();
      const STATUS_STYLES = {
        PR:     { background: '#FFF7ED', border: '1px solid #FED7AA', color: '#9A3412' },
        F:      { background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' },
        Q_HOLD: { background: '#FEFCE8', border: '1px solid #FDE68A', color: '#713F12' },
      };
      if (STATUS_STYLES[s]) return STATUS_STYLES[s];
      // Fallback keyword matching
      if (/SUCCESS|PAID|COMPLETED|PROCESSED/.test(s)) {
        return { background: '#F0FDF4', border: '1px solid #86EFAC', color: '#166534' };
      }
      if (/FAIL|ERROR|REJECT|DECLINED/.test(s)) {
        return { background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' };
      }
      if (/PENDING|PROGRESS|INITIATED/.test(s)) {
        return { background: '#FFF7ED', border: '1px solid #FED7AA', color: '#9A3412' };
      }
      return { background: '#F5F5F5', border: '1px solid #E0E0E0', color: '#616161' };
    },

    close() {
      this.stopPolling();
      this.$emit('input', false);
    },

    reset() {
      this.stopPolling();
      this.loading = false;
      this.columnHeaders = [];
      this.records = [];
      this.tableFilters = {};
      this.totalCount = 0;
      this.totalAmount = 0;
      this.page = 1;
      this.totalPages = 0;
    }
  }
};
</script>

<style scoped>
.pending-payouts__loader {
  margin-top: -16px;
}

.pending-payouts__summary {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.count-card {
  display: flex;
  align-items: center;
  width: fit-content;
  border-radius: 8px;
  border: 1px solid var(--grey-900);
  background: #F7F7F7;
  padding: var(--space-3) var(--space-5);
}

.count-card__label {
  color: var(--grey-900);
}

.count-card--clickable {
  cursor: pointer;
  transition: background 0.15s ease;
}

.count-card--clickable:hover {
  background: #ececec;
}

.count-card__value {
  color: #828285;
}

.pending-payouts__cell {
  white-space: nowrap;
  font-size: 12.5px !important;
}

.pending-payouts__status-chip {
  font-size: 11px !important;
  letter-spacing: 0.01em;
  height: 20px !important;
  border-radius: 4px !important;
  text-transform: capitalize;
}

.pending-payouts__pagination {
  border-top: 1px solid #e0e0e0;
  padding-top: 8px;
}

::v-deep .pending-payouts-modal__table th {
  font-size: 12px !important;
  font-weight: 500 !important;
  white-space: nowrap;
  background: #fafafa !important;
}
</style>
